import type { ApiErrorBody, ErrorCode, Role, UserDto } from '@bjj/shared';
import { createAuthClient } from 'better-auth/client';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'NETWORK_ERROR',
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

type Listener = (user: UserDto | null) => void;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Return the raw text body (CSV export). */
  text?: boolean;
}

/** BetterAuth error codes the dashboard handles, mapped onto the API's own codes. */
const AUTH_CODES: Record<string, ErrorCode> = {
  INVALID_EMAIL_OR_PASSWORD: 'INVALID_CREDENTIALS',
  INVALID_PASSWORD: 'INVALID_CREDENTIALS',
  INVALID_TOKEN: 'TOKEN_INVALID',
  PASSWORD_TOO_SHORT: 'VALIDATION_ERROR',
  PASSWORD_TOO_LONG: 'VALIDATION_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  ACCOUNT_INACTIVE: 'ACCOUNT_INACTIVE',
  INVITATION_INVALID: 'INVITATION_INVALID',
  CONFLICT: 'CONFLICT',
};

const AUTH_MESSAGES: Partial<Record<ErrorCode, string>> = {
  INVALID_CREDENTIALS: 'Incorrect email or password',
  TOKEN_INVALID: 'This link is invalid or has expired. Request a new one.',
  RATE_LIMITED: 'Too many attempts. Please wait a few minutes and try again.',
};

/**
 * Browser API client. Sign-in, sign-out and password flows go through BetterAuth's client; the
 * session itself is an HttpOnly, SameSite=Strict cookie that page scripts can never read, sent
 * automatically on same-origin requests. A 401 from the API means the session has ended.
 */
export function createApiClient(opts: { baseUrl?: string; fetchFn?: typeof fetch } = {}) {
  const baseUrl = opts.baseUrl ?? '/api/v1';
  const fetchFn = opts.fetchFn ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
  const auth = createAuthClient({
    basePath: `${baseUrl}/auth`,
    fetchOptions: { customFetchImpl: fetchFn, credentials: 'same-origin' },
  });
  let user: UserDto | null = null;
  const listeners = new Set<Listener>();

  function setUser(next: UserDto | null) {
    user = next;
    listeners.forEach((l) => l(user));
  }

  async function send(path: string, options: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = {};
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    try {
      return await fetchFn(`${baseUrl}${path}`, {
        method: options.method ?? 'GET',
        headers,
        body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
        credentials: 'same-origin',
      });
    } catch {
      throw new ApiError(0, 'NETWORK_ERROR', 'Could not reach the server. Check your connection.');
    }
  }

  async function toError(res: Response): Promise<ApiError> {
    const body = (await res.json().catch(() => null)) as ApiErrorBody | null;
    return new ApiError(
      res.status,
      body?.error?.code ?? 'INTERNAL',
      body?.error?.message ?? `Request failed (${res.status})`,
      body?.error?.details,
    );
  }

  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const res = await send(path, options);
    if (!res.ok) {
      if (res.status === 401) setUser(null);
      throw await toError(res);
    }
    if (res.status === 204) return undefined as T;
    return (options.text ? await res.text() : await res.json()) as T;
  }

  /** Runs a BetterAuth call, turning its `{ data, error }` result into a value or an ApiError. */
  async function authCall<T>(
    call: () => Promise<{
      data: T | null;
      error: { status: number; code?: string; message?: string } | null;
    }>,
  ): Promise<T> {
    let result: Awaited<ReturnType<typeof call>>;
    try {
      result = await call();
    } catch {
      throw new ApiError(0, 'NETWORK_ERROR', 'Could not reach the server. Check your connection.');
    }
    const { data, error } = result;
    if (error) {
      const code =
        error.status === 429 ? 'RATE_LIMITED' : (AUTH_CODES[error.code ?? ''] ?? 'INTERNAL');
      throw new ApiError(
        error.status,
        code,
        AUTH_MESSAGES[code] ?? error.message ?? `Request failed (${error.status})`,
      );
    }
    return data as T;
  }

  /** Loads the signed-in account, or null when there is no valid session. */
  async function restore(): Promise<UserDto | null> {
    try {
      const { user: me } = await request<{ user: UserDto }>('/me');
      setUser(me);
      return me;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  }

  return {
    request,
    restore,
    get user() {
      return user;
    },
    subscribe(listener: Listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    async login(email: string, password: string): Promise<UserDto> {
      await authCall(() => auth.signIn.email({ email, password }));
      const me = await restore();
      if (!me) throw new ApiError(401, 'UNAUTHENTICATED', 'Sign-in failed. Please try again.');
      return me;
    },
    previewInvitation(token: string) {
      return authCall(() =>
        auth.$fetch<{ email: string; name: string; role: Role }>('/invitation/preview', {
          method: 'POST',
          body: { token },
        }),
      );
    },
    async acceptInvitation(token: string, name: string, password: string): Promise<UserDto> {
      const { user: created } = await authCall(() =>
        auth.$fetch<{ user: UserDto }>('/invitation/accept', {
          method: 'POST',
          body: { token, name, password },
        }),
      );
      setUser(created);
      return created;
    },
    async requestPasswordReset(email: string): Promise<void> {
      await authCall(() => auth.requestPasswordReset({ email }));
    },
    async resetPassword(token: string, newPassword: string): Promise<void> {
      await authCall(() => auth.resetPassword({ token, newPassword }));
    },
    async logout() {
      try {
        await authCall(() => auth.signOut());
      } finally {
        setUser(null);
      }
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
export const api = createApiClient();
